// LooksUsersPosts.js

import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';

import { FaUser } from 'react-icons/fa';

import LooksPost from './LooksPost';
import { getAllDataFromDB } from '../utils/dbUtilities';
import { LooksPostsContext } from '../contexts/LooksPostsContext';

import './HomePage.css';               // keeps styling identical to the main feed

function LooksUsersPosts() {
  /* ─────────── hooks & context ─────────── */
  const { username } = useParams();
  const { deletePostById } = useContext(LooksPostsContext);   // deletion helper
  const [userPosts, setUserPosts] = useState([]);
  const [loading, setLoading]   = useState(true);

  /* ─────────── side-effects ─────────── */
  useEffect(() => {
    let isMounted = true;                // guard against setState on unmount
    window.scrollTo(0, 0);
    document.title = `${username}'s posts`;

    async function fetchUserPosts() {
      try {
        const allPosts = await getAllDataFromDB('looksPosts');

        const specificUserPosts = allPosts
          .filter(
            (post) => post.author === username && !post.deleted && !post.isReported
          )
          .map((post) => ({
            ...post,
            timestamp: post.timestamp?.toDate
              ? post.timestamp.toDate()
              : new Date(post.timestamp)
          }))
          .sort((a, b) => b.timestamp - a.timestamp);  // newest first

        if (isMounted) {
          setUserPosts(specificUserPosts);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching user posts:', err);
        if (isMounted) setLoading(false);
      }
    }

    fetchUserPosts();
    return () => {
      isMounted = false;                 // clean-up flag
    };
  }, [username]);

  /* ─────────── handlers ─────────── */
  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await deletePostById(postId);                         // 1) mark as deleted (Firestore & context)
      setUserPosts((prev) => prev.filter((p) => p.id !== postId)); // 2) optimistic local update
    } catch (err) {
      console.error('Error deleting post:', err);
    }
  };

  /* ─────────── render ─────────── */
  return (
    <div className="posts">
      <h1 style={{ textAlign: 'center', marginBottom: '30px' }}>
        <FaUser /> Posts by {username}
      </h1>

      {loading ? (
        <p style={{ textAlign: 'center' }}>Loading…</p>
      ) : userPosts.length === 0 ? (
        <div className="no-posts-message" style={{ textAlign: 'center' }}>
          <p>No posts found for {username}</p>
        </div>
      ) : (
        userPosts.map((post) => (
          <div key={post.id} className="post-container">
            <LooksPost {...post} onDelete={() => handleDeletePost(post.id)} />
          </div>
        ))
      )}
    </div>
  );
}

export default LooksUsersPosts;
